import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';
import { useQueryUI } from '@/sections/insights/queryBuilder/state/QueryUIContext';

/**
 * Custom hook for fetching filter values with loading state management.
 *
 * Lazy-loads values from filterDef.getAvailableValues() when the hook is first called.
 * Handles loading state, error state, and race condition cleanup.
 *
 * @param filterDef - Filter definition containing optional getAvailableValues function
 * @param axios - Authenticated axios instance for API calls
 * @returns Object with options array, loading boolean, and error string
 *
 * @example
 * const axios = useAxios();
 * const { options, isLoading, error } = useFilterValues(fieldDef, axios);
 */
export function useFilterValues(
  filterDef: SimpleFilterDefinition | undefined,
  axios: AxiosInstance
): {
  options: string[];
  isLoading: boolean;
  error: string | null;
} {
  const { isGlobal } = useQueryUI();
  const [options, setOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when field changes (fixes dropdown persistence bug)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOptions([]);
    setError(null);

    const shouldLoad = filterDef?.getAvailableValues; // Function exists

    if (!shouldLoad) {
      return;
    }

    let cancelled = false;

    const fetchValues = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const values = await filterDef.getAvailableValues!(axios, { isGlobal });
        if (!cancelled) {
          setOptions(values);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load field values:', err);
          setError('Failed to load options');
          setIsLoading(false);
        }
      }
    };

    fetchValues();

    return () => {
      cancelled = true;
    };
  }, [filterDef?.apiField, axios, isGlobal]);

  return { options, isLoading, error };
}
